import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FileX, Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dialogWide } from "@/lib/dialogSize";

import { QUERY_KEYS } from "../../constants/QueryKeys";
import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import { MigrationStatus } from "../../models/MigrationStatus";
import { SourceLink } from "../../models/SourceLink";
import SourceLinksRepository from "../../repositories/SourceLinksRepository";
import MigrationStatusChip from "../common/MigrationStatusChip";
import TableEmptyState from "../common/TableEmptyState";
import TablePagination from "../common/Table/TablePagination";

const sizeFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/**
 * The files one link produced, as the client that discovered them attributed them. Empty for a link reported by
 * an exceptions-only client, which never attributes: the relation is written by full-map clients after the files
 * and the links have both landed.
 */
export const SourceLinkFilesModal: React.FC<{
  link: SourceLink | null;
  organizationId: string;
  onClose: () => void;
}> = ({ link, organizationId, onClose }) => {
  const repository = new SourceLinksRepository();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);

  const filesQuery = useQuery({
    queryKey: [QUERY_KEYS.SOURCE_LINK_FILES, organizationId, link?.entityType, link?.id, page, rowsPerPage],
    enabled: !!link,
    queryFn: () => repository.getLinkFiles(link!.entityType, link!.id, page + 1, rowsPerPage),
  });

  const files = filesQuery.data?.data ?? [];
  const total = filesQuery.data?.paging?.totalCount ?? files.length;

  return (
    <Dialog
      open={!!link}
      onOpenChange={(next) => {
        if (!next) {
          setPage(0);
          onClose();
        }
      }}
    >
      <DialogContent className={`${dialogWide} flex flex-col overflow-hidden`}>
        <DialogHeader>
          <DialogTitle>Files from this link</DialogTitle>
          <DialogDescription className="break-all font-mono text-xs">{link?.linkPath}</DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="flex min-w-0 flex-col overflow-hidden">
          {filesQuery.isFetching && files.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading files&hellip;
            </div>
          ) : filesQuery.isError ? (
            <TableEmptyState icon={AlertTriangle} title="Could not load files" description="The link's files could not be fetched." />
          ) : files.length === 0 ? (
            <TableEmptyState
              icon={FileX}
              title="No files attributed to this link"
              description={
                link && link.filesProducedCount > 0
                  ? `The last scan reported ${link.filesProducedCount} file${link.filesProducedCount === 1 ? "" : "s"} from this link, but the client has not attributed them. Attribution is written by clients reporting the full link map.`
                  : "The last scan produced no files from this link."
              }
            />
          ) : (
            <>
              <div className="min-w-0 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Upload path</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Size (MB)</TableHead>
                      <TableHead>Modified</TableHead>
                      <TableHead>Migrated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {files.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell className="max-w-[22rem] break-all font-mono text-xs" title={file.filePath}>
                          {file.filename || file.filePath}
                        </TableCell>
                        <TableCell className="max-w-[16rem] truncate text-xs" title={file.uploadPathFromRoot}>
                          {file.uploadPathFromRoot}
                        </TableCell>
                        <TableCell>
                          <MigrationStatusChip status={file.status as MigrationStatus} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{sizeFormat.format(file.fileSizeInMegabytes)}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {file.fileModifiedOn ? convertUtcToLocal(file.fileModifiedOn) : <span>&mdash;</span>}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {file.lastMigratedOn ? convertUtcToLocal(file.lastMigratedOn) : <span>&mdash;</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <TablePagination
                page={page}
                rowsPerPage={rowsPerPage}
                rows={total}
                onPageChange={setPage}
                onRowsPerPageChange={(next) => {
                  setRowsPerPage(next);
                  setPage(0);
                }}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SourceLinkFilesModal;
